import { z } from 'zod';
import { apiEnvelope } from '../envelope';

// The member-LOOKUP contract: "find this person", answered with enough to
// recognise and open them and nothing else.
//
// It is deliberately NOT the member-list contract. The list answers "what is on
// my caseload", so its row carries the caseload facts a care person filters and
// sorts on — programmes, last check-in and its moods, last seen, next call,
// onboarding, care interest, next contact due. Lookup reaches ANY member in the
// estate, so returning those would hand every searcher the clinical state of
// every member they can name. One route with two response shapes would give one
// contract two schemas (weaker under ADR-061) and would leave the eventual
// justification gate no seam to attach to — the gate belongs on "reach a member
// who is not yours", which is this surface and not the list.
//
// Withheld on purpose: phoneNumber, status, createdAt, programs, lastCheckInAt,
// lastCheckInMoods, lastSeenAt, nextCallAt, nextCallProvider,
// onboardingCompletedAt, careInterest, nextContactDueDate. That list is NOT
// restated as a test fixture anywhere — the drift guard DERIVES it as
// `keys(memberListItemSchema) \ keys(memberSearchItemSchema)`, so a caseload
// column added to the list later is withheld here by construction rather than
// by someone remembering. Enumerating it would already have missed two:
// `nextContactDueDate` postdates the ticket that specified this endpoint.
//
// Phone stays a MATCHER without becoming a RESULT field: `q` still matches it
// through the projection's generated `search_text`, and it never reaches the
// wire. Pelago 1 draws the same line.

/**
 * The `member_list_projections` columns this read puts on the WIRE — not every
 * column it selects. The repo also reads `member_created_at` to mint the keyset
 * cursor, so the projection is five fields and the query is six; the sixth is
 * deliberately absent from the response schema. Tied to the table by the drift
 * guard (search.drift.test.ts) so a rename or removal breaks a test rather than
 * the wire.
 */
export const MEMBER_SEARCH_PROJECTION_FIELDS = [
  'memberId',
  'email',
  'legacyBackend2Sub',
  'legalFirstName',
  'legalLastName',
] as const;

// Deliberately NOT `.strict()` — ADR-135 Rule 1 forbids it on a response schema
// a client parses, and Atlas parses this one. A strict client contract turns a
// future additive server field into a total failure of the lookup surface for
// every already-loaded tab, rather than an ignored key.
//
// The no-leak guarantee does not depend on strictness and is not weakened by
// this: `sendOk` sends `schema.parse(...)`'s OUTPUT, and a non-strict Zod object
// STRIPS unknown keys, so an un-declared field is removed before the response is
// written. Strict would have turned the same mistake into a 500 — louder, but a
// worse outcome for a surface whose job is to stay available.
//
// `memberId` is the projection's uuid primary key and is never null. The other
// four mirror their columns' nullability: a member whose eligibility has never
// matched has no `member_profiles` row at all, so both legal-name parts are
// absent for the common case, and a member minted without a backend2 counterpart
// carries no `legacyBackend2Sub`. The client falls back to an email-derived
// label rather than rendering blank.
export const memberSearchItemSchema = z.object({
  memberId: z.string(),
  email: z.string().nullable(),
  // The Pelago 1.0 crosswalk. On the wire even though today's lookup card does
  // not draw it: a Pelago 2.0 hit has to be reconcilable with its Pelago 1.0
  // counterpart, or the same person reads as two unrelated results.
  legacyBackend2Sub: z.string().nullable(),
  legalFirstName: z.string().nullable(),
  legalLastName: z.string().nullable(),
});
export type MemberSearchItem = z.infer<typeof memberSearchItemSchema>;

// Keyset-paginated page; `nextCursor` is null on the last page. The cursor is
// the member-list cursor pinned to `createdAt`/`desc` — see the query schema.
export const memberSearchDataSchema = z.object({
  items: z.array(memberSearchItemSchema),
  nextCursor: z.string().nullable(),
});
export type MemberSearchData = z.infer<typeof memberSearchDataSchema>;

export const memberSearchResponseSchema = apiEnvelope(memberSearchDataSchema);
export type MemberSearchResponse = z.infer<typeof memberSearchResponseSchema>;

// The lookup query params (wire shape — optional strings, validated and
// clamped/decoded server-side).
//
// There is deliberately no `sort`, no `order`, and no filter axis. Lookup
// answers "find this person"; an orderable, filterable result set is surface
// area with no reader, and every axis added here is another way to enumerate the
// estate. Ordering is pinned server-side to the list's default (`createdAt`
// DESC), which is what lets the existing keyset cursor be reused unchanged.
//
// `.strict()` is correct HERE and nowhere else in this file: this is a REQUEST
// schema, and ADR-135 Rule 1 scopes strictness to input the server rejects.
//
// `q` is REQUIRED in effect though optional on the wire: an absent, blank, or
// sub-minimum term returns an empty page rather than every member. That is a
// server property, not a client courtesy, and it is enforced in two places —
// neither of them the handler, which passes `q` straight through: the service
// short-circuits an absent or blank term before the repo is reached
// (`searchMembers`), and the repo's predicate builder answers `match-none`
// below the minimum length, because that threshold is a property of the
// trigram index rather than of this surface.
export const memberSearchQuerySchema = z
  .object({
    q: z.string().optional(),
    limit: z.string().optional(),
    cursor: z.string().optional(),
  })
  .strict();
export type MemberSearchQuery = z.infer<typeof memberSearchQuerySchema>;
